import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined"
import { Box, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import Marquee from "react-fast-marquee"

import type { ButtonProps, FC, FCWC, ResponsiveImageType } from ".."
import { Button, Container, Link, ResponsiveImage, SvgIcon } from ".."
import { LinkType } from "../../graphql/types"

export type SlideshowItemType = {
  title: string
  image: ResponsiveImageType
  link: LinkType
}

export type SlideshowProps = {
  title: string
  subtitle: string
  link?: LinkType
  items?: SlideshowItemType[] | null
}

const Slideshow: FC<SlideshowProps> = ({ title, subtitle, link, items }) => {
  return (
    <Container maxWidth="100%" py={{ xs: 12, md: 24 }} px={{ xs: 0, md: 0 }}>
      <SlideshowHeader>
        <SlideshowSubtitle>{subtitle}</SlideshowSubtitle>
        <SlideshowTitle>{title}</SlideshowTitle>
        {link && <SlideshowCTA href={link?.uri}>{link?.title}</SlideshowCTA>}
      </SlideshowHeader>
      <SlideshowSlides>
        {items?.map((item, i) => (
          <SlideshowSlide key={i} {...item} />
        ))}
      </SlideshowSlides>
    </Container>
  )
}
export default Slideshow

/** A sub-component for the explort list header. */
const SlideshowHeader: FCWC = ({ children }) => (
  <Container>
    <Stack maxWidth={1065} mx="auto" alignItems="center">
      {children}
    </Stack>
  </Container>
)

/** A sub-component for the explort list header. */
const SlideshowSubtitle: FCWC = ({ children }) => (
  <Typography variant="body.lg" component="h3" textAlign="center" mb={{ xs: 5, md: 10 }}>
    {children}
  </Typography>
)

/** A sub-component for the explort list title. */
const SlideshowTitle: FCWC = ({ children }) => (
  <Typography variant="heading.3xl" component="h2" textAlign="center" mb={{ xs: 10, md: 15 }}>
    {children}
  </Typography>
)

/** A sub-component for the explort list CTA button. */
const SlideshowCTA: FC<ButtonProps> = (props) => <Button variant="default" {...props} />

/** A sub-component for the explort list slide container. */
const SlideshowSlides: FCWC = ({ children }) => {
  return (
    <Box component={Marquee} py={5} pauseOnHover mt={{ xs: 6, md: 14 }}>
      <Stack direction="row" columnGap={{ xs: 3, md: 10 }}>
        {children}
      </Stack>
    </Box>
  )
}

/** A sub-component for the explort list slide. */
const SlideshowSlide: FC<SlideshowItemType> = ({ title, image, link }) => {
  return (
    <Stack
      flex="0 0 auto"
      p={3}
      pr={{ xs: 10, md: 20 }}
      columnGap={{ xs: 5, md: 10 }}
      direction="row"
      alignItems="center"
      position="relative"
      sx={(theme) => ({
        "isolation": "isolate",
        "transition": theme.transitions.create(["box-shadow"]),

        "& img": {
          transition: theme.transitions.create(["transform"]),
        },

        "&:hover": {
          boxShadow: `0px 0px 20px 0px ${alpha(theme.palette["neutral.900"].main, 0.08)}`,
          img: {
            transform: "scale(1.05)",
          },
        },
      })}
    >
      {link && <Link type="overlay" href={link.uri} title={link.title} />}
      <Box
        borderRadius={1.5}
        overflow="hidden"
        sx={{ width: { xs: 120, md: 180 }, height: { xs: 120, md: 180 } }}
      >
        {image ? (
          <ResponsiveImage image={image} display="block" />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            bgcolor={(theme) => theme.palette["neutral.300"].main}
            borderRadius={1.5}
            sx={{ width: 180, height: 180 }}
          >
            <ImageNotSupportedOutlinedIcon />
          </Stack>
        )}
      </Box>

      <Stack direction="column" rowGap={3}>
        <SvgIcon variant="arrowOutward" size={{ xs: 12, md: 16 }} />
        <Typography variant="heading.lg" component="h2">
          {title}
        </Typography>
      </Stack>
    </Stack>
  )
}
